import {
  Box,
  Card,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Grid,
  Tooltip,
  Button,
  Badge,
  styled,
} from '@mui/material';
import TitleText from '../../components/formlib/TitleText';
import SearchBox from '../../components/formlib/SearchBox';
import { HeadCell, TableHeader } from '../../components/formlib/TableHeader';
import TooltipTableCell from '../../components/formlib/TooltipTableCell';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactComponent as EditIcon } from '../../assets/images/editvector.svg';
import { ReactComponent as Filtericon } from '../../assets/images/FilterIconsvg.svg';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Employee,
  LocalProgram,
  getEmployeeList,
} from '../../services/configApi/employees/employeeServices';
import { formatName, getFullName, getRoleName } from '../../utils/nameUtils';
import { OptionType, Order, ResponseType } from '../../utils/type';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import { PAGE_SIZE } from '../../services/Constant';
import { useNavigate } from 'react-router';
import { EDIT_EMPLOYEE_ROUTE_NAME } from '../../routes/Routing';
import { debounce } from 'lodash';
import SearchNotFound from '../../components/formlib/SearchNotFound';
import { ApiError, isCustomError } from '../../services/ApiResponseHandler';
import AppPagination from '../../components/shared/AppPagination';
import { checkPermissionForFeature } from '../../utils/checkPermission';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';

import CheckBoxComponent from '../../components/formlib/CheckBoxComponent';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import MultipleAutocompleteSearch from '../../components/formlib/MultipleAutocompleteSearch';
import { FormattedMessage } from 'react-intl';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import CustomMenu from '../../components/formlib/CustomMenu';
import { setEmployeeListFilter } from '../../redux/features/employeeList/employeeListFilterSlice';
import DisplayFilter, { FilterBy } from '../../components/shared/DisplayFilter';
import { store } from '../../redux/store';
interface Data {
  name: string;
  program: string;
  role: string;
  phoneNumber: string;
  email: string;
  emergencyContanct: string;
  emergencyPhone: string;
  status: string;
  actions: string;
}

const statusFilters: OptionType[] = [
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
];

const initialHeadCells: HeadCell<Data>[] = [
  {
    id: 'name',
    labelId: 'Parent.Name',
    defaultLabelId: 'Name',
    numeric: false,
    requiredSorting: true,
  },
  {
    id: 'program',
    labelId: 'programText',
    defaultLabelId: 'Program',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'role',
    labelId: 'roleText',
    defaultLabelId: 'Role',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'phoneNumber',
    labelId: 'Contacts.phone',
    defaultLabelId: 'Phone Number',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'email',
    labelId: 'Contacts.email',
    defaultLabelId: 'Email',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'emergencyContanct',
    labelId: 'emergencyContactText',
    defaultLabelId: 'Emergency Contact',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'emergencyPhone',
    labelId: 'emergencyPhoneText',
    defaultLabelId: 'Emergency Phone',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'status',
    labelId: 'Status',
    defaultLabelId: 'Status',
    numeric: false,
    requiredSorting: false,
  },
];

const EmployeeListScreen = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [paginationCount, setPaginationCount] = useState<number>(0);
  const [successOrError, setSuccessOrError] = useState<ResponseType>('success');
  const [toastrId, setToastrId] = useState<string>();
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState<string>();
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Data>('name');
  const [headCells, setHeadCells] = useState(initialHeadCells);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const MemorizedCustomMenu = useMemo(() => CustomMenu, []);
  const { toggleLoader } = React.useContext(LoaderContext) as LoaderContextType;

  const dispatch = useAppDispatch();
  const employeeListFilters = useAppSelector(
    (state) => state.employeeListFilter,
  );
  const [selectedStatus, setSelectedStatus] = useState<OptionType[]>(
    employeeListFilters.status.find((item) => item.key === 'statusText')
      ?.value as OptionType[],
  );

  const handleButtonClick = (employee: Employee) => {
    dispatch(setEmployeeListFilter({ ...employeeListFilters, searchText: '' }));
    navigate(EDIT_EMPLOYEE_ROUTE_NAME, {
      replace: false,
      state: {
        employee: employee,
        screenFrom: 'employeeList',
      },
    });
  };

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof Data,
  ) => {
    setOrder(order === 'asc' ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const isValidInput = (value: string) => {
    const allowedCharacters = /^[a-zA-Z0-9,'.\s!-]*$/;
    return allowedCharacters.test(value);
  };

  const changeTextDebouncer = useCallback(
    debounce((value: string) => {
      if (isValidInput(value)) {
        dispatch(
          setEmployeeListFilter({
            ...store.getState().employeeListFilter,
            pageNo: 0,
            searchText: value,
          }),
        );
      } else {
        setSuccessOrError('error');
        setToastrId('Dashboard.ClientList.searchError');
      }
    }, 500),
    [],
  );

  const handlefilter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handlefilterClose = () => {
    setAnchorEl(null);
  };

  const applyFilter = () => {
    const filterDataSet: FilterBy[] = [];
    if (selectedStatus.length > 0) {
      filterDataSet.push({
        key: 'statusText',
        value: selectedStatus,
      });
    }
    dispatch(
      setEmployeeListFilter({
        ...employeeListFilters,
        status: filterDataSet,
        pageNo: 0,
      }),
    );
  };

  useEffect(() => {
    toggleLoader(true);
    const filterValues = employeeListFilters.status.flatMap((filter) => {
      if (typeof filter.value !== 'string' && filter.value.length > 0) {
        return filter.value.map((value: OptionType) => value.label);
      }
      return [];
    });
    getEmployeeList(
      employeeListFilters.searchText || '',
      employeeListFilters.pageNo || 0,
      PAGE_SIZE,
      orderBy,
      order,
      filterValues,
    )
      .then((response) => {
        if (response.employeeCount === 0) {
          toggleLoader(false);
          setEmployees([]);
          setPaginationCount(0);
          return;
        }
        setPaginationCount(response.employeeCount);
        setEmployees(response.employees);

        toggleLoader(false);
      })
      .catch((error) => {
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('loadFailureMessage');
          setToastrDefaultMessage('Failed to load details');
        }
      });
  }, [employeeListFilters, order]);

  useEffect(() => {
    if (
      headCells.length === 8 &&
      checkPermissionForFeature('backend.employee', 'editPermission')
    ) {
      setHeadCells((prevHeadCells) => [
        ...prevHeadCells,
        {
          id: 'actions',
          labelId: 'action',
          defaultLabelId: 'Action',
          numeric: false,
          requiredSorting: false,
        },
      ]);
    }
    return () => {
      setHeadCells(initialHeadCells);
    };
  }, []);

  const getPrograms = (programs: LocalProgram[] | null): string => {
    if (programs === null || programs.length == 0) {
      return '-';
    }
    const programList: string[] = [];
    programs.forEach((program) => {
      if (program.acronym !== null) {
        programList?.push(program.acronym);
      }
    });
    return programList.join(', ');
  };

  const CustomBadge = styled(Badge)(({ color }: { color?: string }) => ({
    '& .MuiBadge-badge': {
      backgroundColor: color ?? '#37D183',
      height: '8px',
      width: '8px',
      top: 0,
      bottom: 0,
      marginRight: '10px',
    },
  }));

  const badgePicker = (status: string) => {
    switch (status) {
      case 'Inactive':
        return (
          <CustomBadge
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: 'red',
              },
            }}
          />
        );
      case 'Pending':
        return (
          <CustomBadge
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: '#FFA500',
              },
            }}
          />
        );
      default:
        return <CustomBadge variant="dot" />;
    }
  };

  return (
    <Box component="main">
      {toastrId && (
        <SnackBarComponent
          open={toastrId !== undefined}
          handleClose={() => {
            setToastrId(undefined);
          }}
          successOrError={successOrError}
          labelId={toastrId}
          defaultMessageId={toastrDefaultMessage}
        />
      )}

      <Box className="rowContainer">
        <Grid container>
          <Grid item xs={4} lg={4}>
            <TitleText
              labelid="employeeListText"
              defaultlabel="Employee List"
            />
          </Grid>
          <Grid item xs={8} lg={8} className="text-align-end">
            <SearchBox
              labelId="searchEmployeeText"
              defaultlabel="Search Employee"
              onChange={changeTextDebouncer}
              className="mr-md"
            />
            <Button
              className="btn-primary btn-filter"
              variant="outlined"
              onClick={handlefilter}
              endIcon={<KeyboardArrowDownIcon />}
              startIcon={<Filtericon />}
            >
              <FormattedMessage id="FiterText" defaultMessage="Filter" />
            </Button>
            <MemorizedCustomMenu
              id="demo-customized-menu"
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handlefilterClose}
            >
              <Box>
                <MultipleAutocompleteSearch
                  labelId="FilterText"
                  defaultId="Filter"
                  data={statusFilters}
                  value={selectedStatus}
                  onSelectItem={(value) => setSelectedStatus(value)}
                  sxProps={{ width: '400px' }}
                />
              </Box>
              <Box
                className="mt-lg"
                sx={{
                  justifyContent: 'right',
                  display: 'flex',
                }}
              >
                <ButtonComponent
                  className="btn-primary btn-cancel btn_width_90px mr-sm"
                  variantType="contained"
                  labelId="Dashboard.filtercancel"
                  defaultLabelId="Cancel"
                  onClick={() => {
                    setAnchorEl(null);
                    if (employeeListFilters.status.length > 0) {
                      const status = employeeListFilters.status.find(
                        (item) => item.key === 'statusText',
                      );
                      if (status) {
                        setSelectedStatus(status.value as OptionType[]);
                      }
                    }
                  }}
                  disabled={false}
                />

                <ButtonComponent
                  className="btn-primary btn-submit btn_width_90px"
                  variantType="contained"
                  labelId="Dashboard.filtersubmit"
                  defaultLabelId="Apply"
                  onClick={() => {
                    setAnchorEl(null);
                    applyFilter();
                  }}
                  disabled={false}
                />
              </Box>
            </MemorizedCustomMenu>
          </Grid>
        </Grid>
      </Box>

      <Box>
        {employeeListFilters.status.length > 0 && (
          <Box sx={{ marginTop: '16px', marginBottom: '16px' }}>
            <DisplayFilter
              filteredData={employeeListFilters.status}
              handleCloseClickArrayType={(main: number, sub: number) => {
                if (
                  typeof employeeListFilters.status[main].value !== 'string'
                ) {
                  const valueArray = employeeListFilters.status[main]
                    .value as OptionType[];
                  const removed = valueArray[sub];
                  const newValue = valueArray.filter((_, idx) => idx !== sub);
                  if (removed) {
                    // Remove from selectedStatus as well
                    setSelectedStatus(
                      selectedStatus.filter(
                        (status) => status.id !== removed.id,
                      ),
                    );

                    // Update reviewActivitySheetFilters immutably
                    let newFilterBy;
                    if (newValue.length === 0) {
                      // Remove the entire filter group if no values left
                      newFilterBy = employeeListFilters.status.filter(
                        (_, idx) => idx !== main,
                      );
                    } else {
                      // Update the value array for this filter group
                      newFilterBy = [...employeeListFilters.status];
                      newFilterBy[main] = {
                        ...newFilterBy[main],
                        value: newValue,
                      };
                    }
                    dispatch(
                      setEmployeeListFilter({
                        ...employeeListFilters,
                        status: newFilterBy,
                      }),
                    );
                  }
                }
              }}
              handleClearClick={() => {
                dispatch(
                  setEmployeeListFilter({
                    ...employeeListFilters,
                    status: [],
                    pageNo: 0,
                  }),
                );
                setSelectedStatus([]);
              }}
              sxProps={{ marginTop: '0px' }}
            />
          </Box>
        )}
      </Box>

      <Box>
        {employeeListFilters.searchText !== '' && employees.length === 0 && (
          <SearchNotFound />
        )}
        {employees.length === 0 && <SearchNotFound />}
        {employees.length !== 0 && (
          <Box>
            <Card>
              <TableContainer>
                <Table>
                  <TableHeader
                    className="listDataTableHead"
                    headerNames={headCells}
                    checkBoxRequired={false}
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                  />
                  <TableBody className="tableRowcss">
                    {employees.map((employee, index) => {
                      return (
                        <TableRow
                          hover
                          onClick={() => {}}
                          key={`employee-${index + 1}`}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor:
                              index % 2 === 0 ? '#ECF9F8' : '#ffffff',
                          }}
                        >
                          <TableCell padding="checkbox">
                            <CheckBoxComponent ischecked={false} />
                          </TableCell>
                          <TooltipTableCell
                            value={formatName(
                              employee.firstName,
                              null,
                              employee.lastName,
                              null,
                            )}
                            visible={true}
                          />
                          <TooltipTableCell
                            value={getPrograms(employee.programs)}
                            visible={true}
                          />
                          <TooltipTableCell
                            value={
                              employee.employeeRole === null ||
                              employee.employeeRole === ''
                                ? '-'
                                : getRoleName(employee.employeeRole)
                            }
                            visible={true}
                          />
                          <TooltipTableCell
                            value={
                              employee.phone1 === null || employee.phone1 === ''
                                ? '-'
                                : employee.phone1
                            }
                            visible={false}
                          />
                          <TooltipTableCell
                            value={
                              employee.email === null || employee.email === ''
                                ? '-'
                                : employee.email
                            }
                            visible={true}
                          />
                          <TooltipTableCell
                            value={getFullName(
                              employee.emergencyFirstName,
                              employee.emergencyLastName,
                            )}
                            visible={true}
                          />
                          <TooltipTableCell
                            value={
                              employee.emergencyPhone === null ||
                              employee.emergencyPhone === ''
                                ? '-'
                                : employee.emergencyPhone
                            }
                            visible={false}
                          />
                          <TableCell
                            className="cursorPointer"
                            sx={{
                              fontFamily: 'Lato-Regular',
                            }}
                          >
                            <Box className="ml-sm">
                              {employee.status
                                ? badgePicker(employee.status)
                                : '-'}
                              {employee.status}
                            </Box>
                          </TableCell>
                          {checkPermissionForFeature(
                            'backend.employee',
                            'editPermission',
                          ) ? (
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                }}
                              >
                                {checkPermissionForFeature(
                                  'backend.employee',
                                  'editPermission',
                                ) && (
                                  <Box
                                    onClick={() => {
                                      handleButtonClick(employee);
                                    }}
                                  >
                                    <Tooltip title="Edit">
                                      <EditIcon />
                                    </Tooltip>
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                          ) : null}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>

            {paginationCount > 10 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <AppPagination
                  pageNumber={employeeListFilters.pageNo}
                  paginationCount={paginationCount}
                  handleChangePage={(_event, newPage) => {
                    if (newPage - 1 !== employeeListFilters.pageNo) {
                      dispatch(
                        setEmployeeListFilter({
                          ...employeeListFilters,
                          pageNo: newPage - 1,
                        }),
                      );
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EmployeeListScreen;
